import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { ClipboardList, Search, ChevronLeft, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 25;

const TYPE_CONFIG = {
  criteria: { label: "Criteria", className: "bg-blue-600/20 text-blue-400 border-blue-500/30" },
  schedule: { label: "Schedule", className: "bg-green-600/20 text-green-400 border-green-500/30" },
  add_zips: { label: "Add Zips", className: "bg-[#f4cc1e]/20 text-[#f4cc1e] border-[#f4cc1e]/30" },
  remove_zips: { label: "Remove Zips", className: "bg-orange-600/20 text-orange-400 border-orange-500/30" },
};

const AdminChangeRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const fetchRequests = async () => {
      try {
        const response = await axios.get(`${API}/admin/change-requests`);
        setRequests(response.data || []);
      } catch (error) {
        console.error("Failed to fetch change requests:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchRequests();
  }, []);

  const formatDate = (isoString) => {
    if (!isoString) return "-";
    const d = new Date(isoString);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const filteredRequests = useMemo(() => {
    let filtered = requests;

    if (filterType !== "all") {
      filtered = filtered.filter((r) => r.request_type === filterType);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (r) =>
          r.customer_name?.toLowerCase().includes(query) ||
          r.message?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [requests, searchQuery, filterType]);

  // Reset to page 1 whenever the filtered result set changes (search/type change)
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterType]);

  const totalPages = Math.max(1, Math.ceil(filteredRequests.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const paginatedRequests = filteredRequests.slice(pageStart, pageStart + PAGE_SIZE);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#f4cc1e]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <ClipboardList size={24} className="text-[#f4cc1e]" />
        <h1 className="text-2xl font-bold text-white font-['Chivo']">Change Requests</h1>
        <span className="text-sm text-white/50">{filteredRequests.length} of {requests.length}</span>
      </div>

      <Card className="bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg">
        <div className="p-4 border-b border-[#1a3a6a] flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <Input
              placeholder="Search by customer or message..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-9 bg-[#0d2a5a] border-[#1a3a6a] text-white placeholder:text-white/40"
            />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-full sm:w-44 h-9 bg-[#0d2a5a] border-[#1a3a6a] text-white text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#091c3c] border-[#1a3a6a]">
              <SelectItem value="all" className="text-white">All Types</SelectItem>
              <SelectItem value="criteria" className="text-white">Criteria</SelectItem>
              <SelectItem value="schedule" className="text-white">Schedule</SelectItem>
              <SelectItem value="add_zips" className="text-white">Add Zips</SelectItem>
              <SelectItem value="remove_zips" className="text-white">Remove Zips</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <CardContent className="p-0">
          {filteredRequests.length === 0 ? (
            <div className="p-12 text-center text-white/50">
              <ClipboardList size={48} className="mx-auto mb-4 opacity-50" />
              <p>{searchQuery || filterType !== "all" ? "No matching requests found" : "No change requests yet"}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-[#1a3a6a]">
                    <TableHead className="bg-[#091c3c] text-[#f4cc1e] uppercase text-[10px] sm:text-xs font-bold">Date</TableHead>
                    <TableHead className="bg-[#091c3c] text-[#f4cc1e] uppercase text-[10px] sm:text-xs font-bold">Customer</TableHead>
                    <TableHead className="bg-[#091c3c] text-[#f4cc1e] uppercase text-[10px] sm:text-xs font-bold">Type</TableHead>
                    <TableHead className="bg-[#091c3c] text-[#f4cc1e] uppercase text-[10px] sm:text-xs font-bold">Message</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedRequests.map((req) => {
                    const typeConfig = TYPE_CONFIG[req.request_type] || { label: req.request_type, className: "" };
                    return (
                      <TableRow key={req.id} onClick={() => setSelected(req)} title="Click to view the full request"
                        className="border-b border-[#1a3a6a] hover:bg-[#0d2a5a] cursor-pointer">
                        <TableCell className="font-mono text-sm text-white whitespace-nowrap">
                          {formatDate(req.created_at)}
                        </TableCell>
                        <TableCell className="text-white text-sm whitespace-nowrap font-medium">
                          {req.customer_name}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={typeConfig.className}>
                            {typeConfig.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-white text-sm max-w-md">
                          <span className="line-clamp-2">{req.message}</span>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
          {filteredRequests.length > PAGE_SIZE && (
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-[#1a3a6a]">
              <span className="text-xs text-white/50">
                Showing {pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, filteredRequests.length)} of {filteredRequests.length}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={safePage <= 1}
                  className="h-8 border-[#1a3a6a] text-white hover:bg-[#1a3a6a] disabled:opacity-40"
                >
                  <ChevronLeft size={14} className="mr-1" /> Prev
                </Button>
                <span className="text-xs text-white/70 whitespace-nowrap">
                  Page {safePage} of {totalPages}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage >= totalPages}
                  className="h-8 border-[#1a3a6a] text-white hover:bg-[#1a3a6a] disabled:opacity-40"
                >
                  Next <ChevronRight size={14} className="ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-[#091c3c] border-[#1a3a6a] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Change Request</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" className={(TYPE_CONFIG[selected.request_type] || {}).className || ""}>
                  {(TYPE_CONFIG[selected.request_type] || {}).label || selected.request_type}
                </Badge>
                {selected.status && <span className="text-xs text-white/60 capitalize">{selected.status}</span>}
              </div>
              <div className="text-xs text-white/50">
                {selected.customer_name ? `${selected.customer_name} · ` : ""}{formatDate(selected.created_at)}
              </div>
              <div className="rounded-lg bg-[#0d2a5a] border border-[#1a3a6a] p-3 max-h-[55vh] overflow-y-auto">
                <p className="text-sm text-white whitespace-pre-wrap break-words">{selected.message || "(no message)"}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminChangeRequests;
